/**
 * Configuration management for the Hobbes platform.
 * Handles environment variables and feature flags.
 */

export interface AppConfig {
  api: {
    baseUrl: string;
    timeout: number;
  };
  analytics: {
    posthogKey: string;
  };
  sentry: {
    dsn: string;
    debug: boolean;
    appVersion: string;
  };
}

const getEnvVar = (key: string, defaultValue: string = ''): string => {
  return import.meta.env[key] || defaultValue;
};

const getBooleanEnvVar = (key: string, defaultValue: boolean = false): boolean => {
  const value = import.meta.env[key];
  if (value === undefined) return defaultValue;
  return value === 'true' || value === '1';
};

const getNumberEnvVar = (key: string, defaultValue: number = 0): number => {
  const value = import.meta.env[key];
  if (value === undefined) return defaultValue;
  const parsed = parseInt(value, 10);
  return isNaN(parsed) ? defaultValue : parsed;
};

/**
 * Application configuration derived from environment variables
 */
export const config: AppConfig = {
  api: {
    baseUrl: getEnvVar('VITE_API_BASE_URL', 'http://localhost:8000'),
    timeout: getNumberEnvVar('VITE_API_TIMEOUT', 30000),
  },
  analytics: {
    posthogKey: getEnvVar('VITE_PUBLIC_POSTHOG_KEY'),
  },
  sentry: {
    dsn: getEnvVar('VITE_SENTRY_DSN'),
    debug: getBooleanEnvVar('VITE_SENTRY_DEBUG', false),
    appVersion: getEnvVar('VITE_APP_VERSION', '1.0.0'),
  },
};

/**
 * Validates that required configuration values are present
 */
export const validateConfig = (): string[] => {
  const errors: string[] = [];

  // API configuration validation
  if (!config.api.baseUrl) {
    errors.push('VITE_API_BASE_URL is required');
  }

  // Firebase is no longer required since we're using API backend exclusively
  // Legacy Firebase configuration validation removed

  return errors;
};

/**
 * Development mode check
 */
export const isDevelopment = (): boolean => {
  return import.meta.env.DEV;
};

/**
 * Production mode check
 */
export const isProduction = (): boolean => {
  return import.meta.env.PROD;
};

/**
 * Always use API backend (Firebase migration complete)
 */
export const shouldUseApiBackend = (): boolean => {
  return true;
};

/**
 * Get the appropriate base URL for API calls
 */
export const getApiBaseUrl = (): string => {
  return config.api.baseUrl;
};

/**
 * Configuration initialization and validation
 */
export const initializeConfig = (): void => {
  const errors = validateConfig();
  
  if (errors.length > 0) {
    console.error('Configuration validation failed:');
    errors.forEach(error => console.error(`  - ${error}`));
    
    if (isProduction()) {
      throw new Error('Invalid configuration detected. Please check environment variables.');
    }
  }

  // Log configuration in development
  if (isDevelopment()) {
    console.log('App Configuration:', {
      api: {
        baseUrl: config.api.baseUrl,
        timeout: config.api.timeout,
      },
      environment: {
        isDevelopment: isDevelopment(),
        isProduction: isProduction(),
      },
    });
  }
};

export default config;